import React, { useState, useEffect } from 'react';
import { getToken } from '../services/authService';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export function PreOrderScreen({ dhaba, etaMinutes, onBack, onOrderPlaced, initialCart = null }) {
  const [menu, setMenu] = useState(null);
  const [cart, setCart] = useState(initialCart || {});
  const [loading, setLoading] = useState(true);
  const [placing, setPlacing] = useState(false);
  const [orderResult, setOrderResult] = useState(null);
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [activeCategory, setActiveCategory] = useState(null);

  // Fetch menu
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/v1/menu/dhaba/${dhaba.id}`);
        const data = await res.json();
        if (data.success && data.menu) {
          setMenu(data.menu);
          if (data.menu.categories?.length > 0) {
            setActiveCategory(data.menu.categories[0].name);
          }
        }
      } catch (err) {
        console.error('Failed to load menu:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [dhaba.id]);

  const addToCart = (item) => {
    setCart(prev => ({
      ...prev,
      [item.id]: {
        ...item,
        quantity: (prev[item.id]?.quantity || 0) + 1,
      },
    }));
  };

  const removeFromCart = (itemId) => {
    setCart(prev => {
      const next = { ...prev };
      if (next[itemId]?.quantity > 1) {
        next[itemId] = { ...next[itemId], quantity: next[itemId].quantity - 1 };
      } else {
        delete next[itemId];
      }
      return next;
    });
  };

  const cartItems = Object.values(cart);
  const totalAmount = cartItems.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const totalItems = cartItems.reduce((sum, i) => sum + i.quantity, 0);
  const maxPrepTime = cartItems.length > 0
    ? Math.max(...cartItems.map(i => i.prep_time_mins || 15))
    : 0;

  const handlePlaceOrder = async () => {
    if (cartItems.length === 0) return;
    setPlacing(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE}/api/orders/place`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          dhaba_id: dhaba.id,
          items: cartItems.map(i => ({
            item_id: i.id,
            name: i.name,
            price: i.price,
            quantity: i.quantity,
            is_veg: i.is_veg,
            prep_time_mins: i.prep_time_mins || 15,
          })),
          eta_minutes: etaMinutes || 30,
          special_instructions: specialInstructions || null,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setOrderResult(data.order);
      }
    } catch (err) {
      console.error('Order failed:', err);
    } finally {
      setPlacing(false);
    }
  };

  // Order confirmation screen
  if (orderResult) {
    const prepLeadMins = Math.max(0, (etaMinutes || 30) - (orderResult.max_prep_mins || 15));
    return (
      <div style={{ minHeight: '100vh', background: '#FFFFFF', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
        <div style={{ textAlign: 'center', maxWidth: '340px' }}>
          {/* Success animation */}
          <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: 'linear-gradient(135deg, #D4AF37, #C5A028)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontSize: '36px' }}>
            ✓
          </div>
          <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#1A1A1A', fontFamily: "'Manrope', sans-serif", marginBottom: '8px' }}>
            Order Placed!
          </h1>
          <p style={{ color: '#6B7280', fontSize: '14px', marginBottom: '24px' }}>
            Your food will be ready when you arrive
          </p>

          {/* ETA sync card */}
          <div style={{ background: 'linear-gradient(135deg, #1A1A1A, #2D2D2D)', borderRadius: '16px', padding: '20px', marginBottom: '20px', textAlign: 'left' }}>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>
              ETA Synchronization
            </p>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
              <div>
                <p style={{ color: '#D4AF37', fontSize: '24px', fontWeight: '700' }}>{etaMinutes || 30} min</p>
                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px' }}>Your ETA</p>
              </div>
              <div style={{ width: '1px', background: 'rgba(255,255,255,0.1)' }} />
              <div>
                <p style={{ color: '#22C55E', fontSize: '24px', fontWeight: '700' }}>{orderResult.max_prep_mins} min</p>
                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px' }}>Prep Time</p>
              </div>
            </div>

            {/* Timeline */}
            <div style={{ position: 'relative', paddingLeft: '20px' }}>
              <div style={{ position: 'absolute', left: '6px', top: '4px', bottom: '4px', width: '2px', background: 'rgba(212,175,55,0.3)' }} />
              <div style={{ marginBottom: '16px', position: 'relative' }}>
                <div style={{ position: 'absolute', left: '-17px', top: '4px', width: '8px', height: '8px', borderRadius: '50%', background: '#D4AF37' }} />
                <p style={{ color: '#FFFFFF', fontSize: '13px', fontWeight: '600' }}>Order placed now</p>
              </div>
              {prepLeadMins > 0 && (
                <div style={{ marginBottom: '16px', position: 'relative' }}>
                  <div style={{ position: 'absolute', left: '-17px', top: '4px', width: '8px', height: '8px', borderRadius: '50%', background: '#F59E0B' }} />
                  <p style={{ color: '#F59E0B', fontSize: '13px', fontWeight: '600' }}>Kitchen starts in {prepLeadMins} min</p>
                </div>
              )}
              <div style={{ position: 'relative' }}>
                <div style={{ position: 'absolute', left: '-17px', top: '4px', width: '8px', height: '8px', borderRadius: '50%', background: '#22C55E' }} />
                <p style={{ color: '#22C55E', fontSize: '13px', fontWeight: '600' }}>Food ready when you arrive</p>
              </div>
            </div>
          </div>

          {/* Order summary */}
          <div style={{ background: '#F9FAFB', borderRadius: '12px', padding: '16px', marginBottom: '20px', textAlign: 'left' }}>
            <p style={{ fontWeight: '600', color: '#1A1A1A', marginBottom: '8px' }}>{orderResult.dhaba_name}</p>
            <p style={{ fontSize: '13px', color: '#6B7280' }}>{orderResult.item_count} items · ₹{orderResult.total_amount}</p>
          </div>

          <button onClick={() => onOrderPlaced && onOrderPlaced(orderResult)} data-testid="order-done-btn"
            style={{ width: '100%', padding: '16px', borderRadius: '14px', border: 'none', background: 'linear-gradient(135deg, #D4AF37, #C5A028)', color: '#1A1A1A', fontWeight: '700', fontSize: '16px', cursor: 'pointer' }}>
            Done
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#FFF' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: '40px', height: '40px', border: '3px solid #E5E7EB', borderTopColor: '#D4AF37', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 12px' }} />
          <p style={{ color: '#6B7280', fontSize: '14px' }}>Loading menu...</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#FFFFFF', paddingBottom: totalItems > 0 ? '140px' : '20px' }}>
      {/* Header */}
      <div style={{ background: 'linear-gradient(180deg, #1A1A1A, #2D2D2D)', padding: '16px', position: 'sticky', top: 0, zIndex: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
          <button onClick={onBack} data-testid="preorder-back-btn"
            style={{ width: '40px', height: '40px', borderRadius: '12px', background: 'rgba(212,175,55,0.1)', border: '1px solid rgba(212,175,55,0.2)', color: '#D4AF37', fontSize: '18px', cursor: 'pointer' }}>
            ←
          </button>
          <div style={{ flex: 1 }}>
            <h1 style={{ fontSize: '16px', fontWeight: '700', color: '#D4AF37', fontFamily: "'Manrope', sans-serif" }}>
              {dhaba.name}
            </h1>
            <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>Pre-order for pickup</p>
          </div>
        </div>

        {/* ETA badge */}
        <div style={{ display: 'flex', gap: '12px' }}>
          <div style={{ flex: 1, background: 'rgba(212,175,55,0.1)', borderRadius: '10px', padding: '10px 12px', border: '1px solid rgba(212,175,55,0.2)' }}>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>Your ETA</p>
            <p style={{ color: '#D4AF37', fontSize: '18px', fontWeight: '700' }}>{etaMinutes || '~30'} min</p>
          </div>
          {maxPrepTime > 0 && (
            <div style={{ flex: 1, background: 'rgba(34,197,94,0.1)', borderRadius: '10px', padding: '10px 12px', border: '1px solid rgba(34,197,94,0.2)' }}>
              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>Max Prep Time</p>
              <p style={{ color: '#22C55E', fontSize: '18px', fontWeight: '700' }}>{maxPrepTime} min</p>
            </div>
          )}
          <div style={{ flex: 1, background: etaMinutes >= maxPrepTime ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)', borderRadius: '10px', padding: '10px 12px', border: `1px solid ${etaMinutes >= maxPrepTime ? 'rgba(34,197,94,0.2)' : 'rgba(239,68,68,0.2)'}` }}>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px' }}>Status</p>
            <p style={{ color: etaMinutes >= maxPrepTime ? '#22C55E' : '#F59E0B', fontSize: '13px', fontWeight: '700' }}>
              {etaMinutes >= maxPrepTime ? 'Ready on arrival' : 'Slight wait'}
            </p>
          </div>
        </div>
      </div>

      {/* No menu */}
      {(!menu || menu.total_items === 0) && (
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <span style={{ fontSize: '48px', display: 'block', marginBottom: '16px' }}>📋</span>
          <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1A1A1A', marginBottom: '8px' }}>Menu Not Available</h3>
          <p style={{ color: '#6B7280', fontSize: '14px' }}>This dhaba hasn't added their menu yet</p>
        </div>
      )}

      {/* Category tabs */}
      {menu && menu.categories?.length > 0 && (
        <>
          <div style={{ display: 'flex', gap: '8px', padding: '16px', overflowX: 'auto', borderBottom: '1px solid #E5E7EB' }}>
            {menu.categories.map(cat => (
              <button key={cat.name} onClick={() => setActiveCategory(cat.name)}
                style={{
                  padding: '8px 18px', borderRadius: '20px', border: 'none', whiteSpace: 'nowrap', cursor: 'pointer',
                  background: activeCategory === cat.name ? '#1A1A1A' : '#F3F4F6',
                  color: activeCategory === cat.name ? '#D4AF37' : '#6B7280',
                  fontWeight: '600', fontSize: '13px',
                }}>
                {cat.name.charAt(0).toUpperCase() + cat.name.slice(1)} ({cat.items.length})
              </button>
            ))}
          </div>

          {/* Menu items */}
          <div style={{ padding: '16px' }}>
            {menu.categories
              .filter(cat => cat.name === activeCategory)
              .map(cat => cat.items.map(item => (
                <div key={item.id} data-testid={`menu-item-${item.id}`}
                  style={{ display: 'flex', gap: '12px', padding: '16px 0', borderBottom: '1px solid #F3F4F6' }}>
                  {/* Item info */}
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                      <span style={{ width: '14px', height: '14px', border: `2px solid ${item.is_veg ? '#22C55E' : '#EF4444'}`, borderRadius: '3px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: item.is_veg ? '#22C55E' : '#EF4444' }} />
                      </span>
                      {item.is_bestseller && (
                        <span style={{ fontSize: '11px', color: '#D4AF37', fontWeight: '600' }}>Bestseller</span>
                      )}
                    </div>
                    <h4 style={{ fontSize: '15px', fontWeight: '600', color: '#1A1A1A', marginBottom: '2px' }}>{item.name}</h4>
                    <p style={{ fontSize: '15px', fontWeight: '700', color: '#1A1A1A', marginBottom: '4px' }}>₹{item.price}</p>
                    {item.description && (
                      <p style={{ fontSize: '12px', color: '#6B7280', lineHeight: '1.4' }}>{item.description}</p>
                    )}
                    <p style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '4px' }}>Prep: {item.prep_time_mins || 15} min</p>
                  </div>

                  {/* Image + Add button */}
                  <div style={{ width: '110px', flexShrink: 0, position: 'relative' }}>
                    {item.image ? (
                      <img src={item.image} alt={item.name}
                        style={{ width: '110px', height: '80px', objectFit: 'cover', borderRadius: '10px' }} />
                    ) : (
                      <div style={{ width: '110px', height: '80px', background: '#F3F4F6', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px' }}>
                        🍽️
                      </div>
                    )}
                    {/* Add/Remove buttons */}
                    <div style={{ position: 'absolute', bottom: '-12px', left: '50%', transform: 'translateX(-50%)' }}>
                      {cart[item.id] ? (
                        <div style={{ display: 'flex', alignItems: 'center', background: '#FFF', borderRadius: '8px', border: '1px solid #D4AF37', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                          <button onClick={() => removeFromCart(item.id)} data-testid={`remove-${item.id}`}
                            style={{ width: '32px', height: '32px', border: 'none', background: 'transparent', color: '#D4AF37', fontWeight: '700', fontSize: '16px', cursor: 'pointer' }}>
                            −
                          </button>
                          <span style={{ padding: '0 8px', fontWeight: '700', color: '#D4AF37', fontSize: '14px' }}>
                            {cart[item.id].quantity}
                          </span>
                          <button onClick={() => addToCart(item)} data-testid={`add-more-${item.id}`}
                            style={{ width: '32px', height: '32px', border: 'none', background: 'transparent', color: '#D4AF37', fontWeight: '700', fontSize: '16px', cursor: 'pointer' }}>
                            +
                          </button>
                        </div>
                      ) : (
                        <button onClick={() => addToCart(item)} data-testid={`add-${item.id}`}
                          style={{ padding: '6px 24px', borderRadius: '8px', border: '1px solid #D4AF37', background: '#FFF', color: '#D4AF37', fontWeight: '700', fontSize: '13px', cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                          ADD
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )))}
          </div>
        </>
      )}

      {/* Cart footer */}
      {totalItems > 0 && (
        <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 30, maxWidth: '28rem', margin: '0 auto' }}>
          {/* Special instructions */}
          <div style={{ background: '#FFF', padding: '8px 16px', borderTop: '1px solid #E5E7EB' }}>
            <input
              type="text"
              value={specialInstructions}
              onChange={(e) => setSpecialInstructions(e.target.value)}
              placeholder="Any special instructions? (optional)"
              data-testid="special-instructions"
              style={{ width: '100%', padding: '10px 12px', background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '13px', outline: 'none' }}
            />
          </div>
          {/* Order button */}
          <div style={{ background: 'linear-gradient(135deg, #1A1A1A, #2D2D2D)', padding: '16px' }}>
            <button onClick={handlePlaceOrder} disabled={placing} data-testid="place-order-btn"
              style={{
                width: '100%', padding: '16px', borderRadius: '14px', border: 'none', cursor: placing ? 'wait' : 'pointer',
                background: 'linear-gradient(135deg, #D4AF37, #C5A028)', color: '#1A1A1A', fontWeight: '700', fontSize: '16px',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
              <span>{placing ? 'Placing Order...' : `Place Pre-Order · ${totalItems} item${totalItems > 1 ? 's' : ''}`}</span>
              <span style={{ fontSize: '18px', fontWeight: '800' }}>₹{totalAmount}</span>
            </button>
            <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontSize: '11px', marginTop: '8px' }}>
              Food will be timed to your arrival
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default PreOrderScreen;
